"use client"

import { useState, useCallback, useId } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Loader2 } from "lucide-react"

interface DeferCommissionModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (date: Date) => void
  isLoading: boolean
  dictionary: any
}

export function DeferCommissionModal({
  open,
  onOpenChange,
  onConfirm,
  isLoading,
  dictionary,
}: DeferCommissionModalProps) {
  const [dateValue, setDateValue] = useState("")
  const [dateError, setDateError] = useState<string | null>(null)
  const errorId = useId()

  // Compute min date as tomorrow in UTC (yyyy-MM-dd format)
  const now = new Date()
  const utcTomorrow = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1))
  const minDate = utcTomorrow.toISOString().split("T")[0]

  const handleDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setDateValue(value)
    if (value) {
      const utcNow = new Date()
      const utcToday = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), utcNow.getUTCDate()))
      const [year, month, day] = value.split("-").map(Number)
      const selectedUtc = new Date(Date.UTC(year, month - 1, day))
      if (selectedUtc <= utcToday) {
        setDateError(dictionary?.dashboard?.messages?.deferDateFuture ?? "Defer date must be in the future.")
      } else {
        setDateError(null)
      }
    } else {
      setDateError(null)
    }
  }, [dictionary])

  const handleConfirm = useCallback(() => {
    if (dateValue && !dateError) {
      const [year, month, day] = dateValue.split("-").map(Number)
      onConfirm(new Date(Date.UTC(year, month - 1, day)))
    }
  }, [dateValue, dateError, onConfirm])

  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (!newOpen) {
      setDateValue("")
      setDateError(null)
    }
    onOpenChange(newOpen)
  }, [onOpenChange])

  const isConfirmDisabled = !dateValue || !!dateError || isLoading

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {dictionary?.dashboard?.actions?.deferModalTitle ?? "Defer to Future"}
          </DialogTitle>
          <DialogDescription>
            {dictionary?.dashboard?.actions?.deferDateLabel ?? "Select a date"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Input
            type="date"
            value={dateValue}
            onChange={handleDateChange}
            min={minDate}
            aria-label={dictionary?.dashboard?.actions?.deferDateLabel ?? "Defer to date"}
            aria-invalid={!!dateError}
            aria-describedby={dateError ? errorId : undefined}
          />
          <div
            id={errorId}
            aria-live="polite"
            className="min-h-[20px] text-sm text-destructive"
          >
            {dateError}
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isLoading}
          >
            {dictionary?.dashboard?.actions?.deferCancel ?? "Cancel"}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
          >
            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />}
            {dictionary?.dashboard?.actions?.deferConfirm ?? "Confirm Defer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
